<!-- Template section -->
<template>
  <div>
    <div class="verify-container my-8">
      <div v-if="loading" class="container main has-text-centered">
        <div class="title is-4 has-text-primary">
          Verifying your login...
        </div>
        <div>
          <i class="fas fa-gear has-text-primary fa-3x fa-spin"></i>
        </div>
      </div>
      <div v-else-if="error">
        <h2>Error</h2>
        <p>{{ error }}</p>
        <router-link to="/login">Return to Login</router-link>
      </div>
    </div>
  </div>
</template>

<script>
// Recommended: Move API calls to a separate service file
// import AuthService from '@/services/AuthService'
// import ProfileService from '@/services/ProfileService'
// import ResourceService from '@/services/ResourceService'

export default {
  name: "MagicLinkVerify",

  data() {
    return {
      loading: true,
      error: null,
    }
  },

  computed: {
    redirectUrl() {
      const redirect_url = this.$route.query.redirect_url
      return redirect_url ? decodeURIComponent(redirect_url) : "home"
    },
    isLoggedIn() {
      return Object.keys(this.$globals.profile).length > 0;
    },
  },

  created() {
    this.verifyMagicLink()
  },

  methods: {
    async verifyMagicLink() {
      const token = this.$route.query.token

      if (!token) {
        this.handleError("Invalid or missing token")
        return
      }

      try {
        // Verify the magic link token
        const verifyResult = await this.verifyToken(token)

        if (!verifyResult || !verifyResult.data || !verifyResult.data.token) {
          throw new Error("Invalid verification response")
        }

        const jwtToken = verifyResult.data.token

        // Fetch user profile with the JWT token
        const profileResult = await this.fetchUserProfile(jwtToken)

        if (!profileResult || !profileResult.data) {
          throw new Error("Failed to fetch user profile")
        }

        // Login the user with the profile data
        await this.loginUser(profileResult.data)
      } catch (err) {
        this.handleError(err)
      } finally {
        this.loading = false
      }
    },

    async verifyToken(token) {
      const baseUrl = process.env.VUE_APP_HUDDLELABS_API_BASE_URL
      const username = process.env.VUE_APP_HUDDLELABS_API_USERNAME
      const password = process.env.VUE_APP_HUDDLELABS_API_PASSWORD
      const basicAuth = btoa(username + ":" + password)

      const headers = new Headers({
        "Authorization": `Basic ${basicAuth}`,
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
      })

      const response = await fetch(
        `${baseUrl}auth/verify-magic-link?token=${token}`,
        {
          method: "GET",
          headers,
          redirect: "follow",
        }
      )

      if (!response.ok) {
        throw new Error(`Verification failed: ${response.status}`)
      }

      return await response.json()
    },

    async fetchUserProfile(jwtToken) {
      const baseUrl = process.env.VUE_APP_HUDDLELABS_API_BASE_URL

      const response = await fetch(`${baseUrl}get-profile`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${jwtToken}`,
        },
      })

      if (!response.ok) {
        throw new Error(`Profile fetch failed: ${response.status}`)
      }

      return await response.json()
    },

    async loginUser(profile) {
      // Sign out any existing user
      this.$app.logout()

      // Get expanded profile details
      const updatedProfile = await this.getFullProfile(profile.id)

      if (!updatedProfile) {
        throw new Error("Failed to get full profile details")
      }

      // Update store with profile information
      this.updateStoreWithProfile(updatedProfile)

      //Initialize hotjar tracking
      this.initializeTracking(updatedProfile);

      // Log the login completed event
      this.logLoginCompleted();

      // Set login expiration
      this.setLoginExpiration()

      // Load necessary resources
      await this.loadResources()

      // Redirect to the appropriate page
      this.redirectToDestination()
    },

    updateStoreWithProfile(profile) {
      // Set the traveller profile
      this.$store.commit("travellersAndTrips/SET_TRAVELLER_PROFILE", profile)

      // Set the auth user
      this.$store.commit("travellersAndTrips/ADD_PROFILE", profile)

      // Set default travellers object
      const travellers = {
        tripType: "business",
        bookFor: "user",
        others: [],
        set: true,
        business: true,
        self: true,
      }

      this.$store.commit("travellersAndTrips/EDIT_TRAVELLERS", travellers)
    },

    setLoginExpiration() {
      const expirationTime = new Date().getTime() + this.$appConfig.routerTimer
      localStorage.setItem("loginExpireTime", expirationTime)
    },

    redirectToDestination() {
      const redirectPath = this.redirectUrl
      this.$router.push({
        path: "/" + redirectPath,
        query: this.$route.query,
      })
    },

    async getFullProfile(id) {
      const body = {
        contactId: id,
        includeRelated: [
          "travelerPrograms",
          "paymentMethods",
          "unusedTickets",
          "travelPolicy",
          "preferredVendors",
          "tripcircuitConfiguration",
        ],
      }

      try {
        const result = await this.$app.huddleLabsServerCall(
          "profile/travel-profile",
          body
        )

        if (result.data && result.data.contactStatus !== "Active") {
          throw new Error(
            "Your TripCircuit account is not active. Please contact your company's travel champion."
          )
        }

        return result.data
      } catch (error) {
        this.handleError(error)
        return null
      }
    },
    logLoginCompleted() {

    // Log the event
    let eventData = {
        event_type: 'Login Completed',
        event_properties: {
          login_status: 'success',
          login_method: 'magic_link',
        }
      }
        
      this.$app.logEvent(eventData);

    },
    initializeTracking(profile) {
  const hotjarUserId = profile.id;

  const hotjarUserData = {
    account_id: profile.account.id,
    account_number: profile.account.gantAccountNumber,
    account_name: profile.account.name,
    traveler_persona: profile.travelerPersona,
    is_vip: profile.isVip,
    is_guest: profile.isGuest,
    is_travel_champion: profile.isTravelChampion,
    meeting_mode: "No",
  };

  console.log("Hotjar", hotjarUserId, hotjarUserData);

  if (typeof window.hj === "function") {
    window.hj("identify", hotjarUserId, hotjarUserData);
  }
},


    async loadResources() {
      try {
        // Load resources in parallel for better performance
        await Promise.all([
          this.loadVendors(),
          this.loadOptions(),
          this.loadAirports(),
          this.loadUnaccompaniedMinorsInfo(),
          this.loadTravelPolicyAttributes(),
        ])
      } catch (error) {
        console.error("Error loading resources:", error)
        // Continue even if resources fail to load
      }
    },

    async loadVendors() {
      const result = await this.$app.huddleLabsServerCall("resources/vendors")
      this.$store.dispatch("bookTravel/vendors", result)
    },

    async loadOptions() {
      const result = await this.$app.huddleLabsServerCall("resources/options")
      this.$store.dispatch("options/setOptions", result.data)
    },

    async loadAirports() {
      const result = await this.$app.huddleLabsServerCall(
        "resources/airports?mode=advanced"
      )
      this.$store.dispatch("bookTravel/airports", result.data)
    },

    async loadUnaccompaniedMinorsInfo() {
      const result = await this.$app.huddleLabsServerCall(
        "resources/airline-unaccompanied-minors"
      )
      this.$store.dispatch(
        "travellersAndTrips/unaccompaniedMinorAirlineDetails",
        result.data
      )
    },

    async loadTravelPolicyAttributes() {
      const result = await this.$app.huddleLabsServerCall(
        "profile/travel-policy-attributes",
        null
      )
      this.$store.dispatch(
        "travelPolicy/addTravelPolicyAttributes",
        result.data
      )
    },

    handleError(error) {
      console.error("Verification error:", error)
      this.error =
        error.message ||
        "An unexpected error occurred during login verification"
      this.loading = false
    },
  },
}
</script>
